document.addEventListener('DOMContentLoaded', () => {
  const moneyFormat = document.documentElement.dataset.moneyFormat || '${{amount}}';
  const currencyCode = document.documentElement.dataset.currencyCode || 'USD';

  const formatMoneyValue = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(Number(cents) || 0, moneyFormat);
    }

    const amount = (Number(cents) || 0) / 100;
    const formattedAmount = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return moneyFormat.replace(/\{\{\s*amount(?:_[^}]*)?\s*\}\}/, formattedAmount);
  };

  const formatMoney = (cents) => `${formatMoneyValue(cents)} ${currencyCode}`;

  document.querySelectorAll('[data-quantity-preset]').forEach((button) => {
    button.addEventListener('click', () => {
      const form = button.closest('form');
      const input = form?.querySelector('input[name="quantity"]');
      const purchaseOptionInput = form?.querySelector('[data-purchase-option-input]');
      const nextValue = Number(button.dataset.quantityPreset || 1);
      const purchaseOptionValue = button.dataset.purchaseOptionValue;

      if (!input) return;

      input.value = String(nextValue);
      if (purchaseOptionInput && purchaseOptionValue) {
        purchaseOptionInput.value = purchaseOptionValue;
      }
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  document.querySelectorAll('[data-set-quantity-selector]').forEach((wrapper) => {
    const input = wrapper.querySelector('[data-set-quantity-input]');
    const form = wrapper.closest('form');
    const actualInput = form?.querySelector('[data-set-quantity-actual]');
    const totalOutput = form?.querySelector('[data-set-quantity-total]');
    const setSize = Number(wrapper.dataset.setSize || 1);
    const maxSets = Number(wrapper.dataset.quantityMax || input?.max || 0);

    if (!input || !actualInput) return;

    const syncSetQuantity = () => {
      let sets = Math.max(1, Number(input.value || 1));
      if (maxSets > 0) {
        sets = Math.min(maxSets, sets);
      }
      const total = sets * setSize;

      input.value = String(sets);
      actualInput.value = String(sets);

      if (totalOutput) {
        totalOutput.textContent = String(total);
      }
    };

    wrapper.querySelectorAll('[data-set-quantity-button]').forEach((button) => {
      button.addEventListener('click', () => {
        const currentValue = Number(input.value || 1);
        const delta = Number(button.dataset.setQuantityButton || 0);
        input.value = String(Math.max(1, currentValue + delta));
        syncSetQuantity();
      });
    });

    input.addEventListener('change', syncSetQuantity);
    syncSetQuantity();
  });

  const updateCartItemUI = (itemNode, lineItem) => {
    if (!itemNode || !lineItem) return;

    itemNode.dataset.unitPrice = String(lineItem.final_price);

    const quantityInput = itemNode.querySelector('.quantity-selector input[type="number"]');
    const lineTotal = itemNode.querySelector('[data-cart-line-total]');
    const lineUnitPrice = itemNode.querySelector('[data-cart-unit-price]');
    const isWholeSet = itemNode.dataset.isWholeSet === 'true';

    if (quantityInput) {
      quantityInput.value = String(Math.max(1, Number(lineItem.quantity || 1)));
      quantityInput.dataset.previousQuantity = quantityInput.value;
    }

    if (lineUnitPrice) {
      lineUnitPrice.textContent = isWholeSet
        ? `${formatMoney(lineItem.final_price)} per set`
        : `${formatMoney(lineItem.final_price)} each`;
    }

    if (lineTotal) {
      lineTotal.textContent = `Total ${formatMoney(lineItem.final_line_price)}`;
    }
  };

  const updateCartSummaryUI = (cart) => {
    document.querySelectorAll('[data-cart-subtotal]').forEach((subtotalNode) => {
      subtotalNode.textContent = formatMoney(cart.total_price);
    });
  };

  document.querySelectorAll('[data-cart-item]').forEach((itemNode) => {
    const quantityInput = itemNode.querySelector('.quantity-selector input[type="number"]');
    const quantityAlert = itemNode.querySelector('[data-cart-quantity-alert]');
    if (!quantityInput) return;

    const minimumQuantity = Math.max(1, Number(quantityInput.min || 1));
    const maximumQuantity = Math.max(0, Number(quantityInput.max || 0));
    const clampQuantity = (value) => {
      let nextValue = Math.max(minimumQuantity, Number(value || minimumQuantity));

      if (maximumQuantity > 0) {
        nextValue = Math.min(maximumQuantity, nextValue);
      }

      return nextValue;
    };

    let quantitySyncTimer = null;
    let quantityRequestInFlight = false;
    let syncedQuantity = clampQuantity(quantityInput.value);
    let desiredQuantity = syncedQuantity;

    const setQuantityAlert = (show) => {
      if (!quantityAlert) return;
      quantityAlert.hidden = !show;
    };

    const syncCartQuantity = async () => {
      const lineIndex = Number(itemNode.dataset.lineIndex || 0);

      if (!lineIndex) return;
      if (quantityRequestInFlight) return;
      if (desiredQuantity === syncedQuantity) return;

      const requestQuantity = desiredQuantity;

      quantityRequestInFlight = true;

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: lineIndex,
            quantity: requestQuantity
          })
        });

        if (!response.ok) {
          throw new Error('Cart update failed');
        }

        const cart = await response.json();
        const updatedLineItem = cart.items[lineIndex - 1];
        const updatedQuantity = clampQuantity(updatedLineItem?.quantity || requestQuantity);

        updateCartItemUI(itemNode, updatedLineItem);
        updateCartSummaryUI(cart);
        syncedQuantity = updatedQuantity;
        quantityInput.dataset.previousQuantity = String(syncedQuantity);
        setQuantityAlert(false);

        if (desiredQuantity !== syncedQuantity) {
          quantityInput.value = String(desiredQuantity);
        }
      } catch (error) {
        desiredQuantity = syncedQuantity;
        quantityInput.value = String(syncedQuantity);
        quantityInput.dataset.previousQuantity = String(syncedQuantity);
      } finally {
        quantityRequestInFlight = false;

        if (desiredQuantity !== syncedQuantity) {
          window.clearTimeout(quantitySyncTimer);
          quantitySyncTimer = window.setTimeout(syncCartQuantity, 120);
        }
      }
    };

    quantityInput.addEventListener('change', () => {
      const rawQuantity = Math.max(minimumQuantity, Number(quantityInput.value || minimumQuantity));
      const clampedQuantity = clampQuantity(rawQuantity);

      desiredQuantity = clampedQuantity;
      quantityInput.value = String(clampedQuantity);
      setQuantityAlert(maximumQuantity > 0 && rawQuantity > maximumQuantity);

      window.clearTimeout(quantitySyncTimer);
      quantitySyncTimer = window.setTimeout(syncCartQuantity, 160);
    });

    quantityInput.value = String(syncedQuantity);
    quantityInput.dataset.previousQuantity = String(syncedQuantity);
    setQuantityAlert(false);
  });

});
